import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn } from '../../components/DataGrid/DataGrid';
import { salesItemService, SalesItemDTO } from '../../services/salesItemService';
import barcodeService, { PdfDisplayError } from '../../services/barcodeService';
import { parseApiError } from '../../utils/errorUtils';
import '../Products/ProductCategoryListPage.css';
import { Printer, RefreshCw, Barcode } from 'lucide-react';

/**
 * Lets the user pick a batch of sales items and print one combined A4 label
 * sheet for all of them at once, using the company's configured barcode
 * sheet design (same design as the single-product Print Barcode dialog).
 */
const SalesItemBarcodePrintPage: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SalesItemDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // salesItemId -> label quantity, present only while the row is selected
  const [selected, setSelected] = useState<Record<number, number>>({});

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await salesItemService.getSalesItems(1, 500);
      const all = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(all)) setItems(all);
    } catch {
      setError(t('salesItem.failedToLoadItem'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await salesItemService.getSalesItems(1, 500);
      const all = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(all)) setItems(all);
    } catch {
      setError(t('salesItem.failedToLoadItem'));
    } finally {
      setRefreshing(false);
    }
  };

  const toggleSelect = (item: SalesItemDTO) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (item.salesItemId in next) {
        delete next[item.salesItemId];
      } else {
        next[item.salesItemId] = 1;
      }
      return next;
    });
  };

  const setQuantity = (salesItemId: number, quantity: number) => {
    const clamped = Math.max(1, Math.min(2000, quantity || 1));
    setSelected((prev) => (salesItemId in prev ? { ...prev, [salesItemId]: clamped } : prev));
  };

  const selectAll = () => {
    const next: Record<number, number> = {};
    items.forEach((item) => { next[item.salesItemId] = selected[item.salesItemId] ?? 1; });
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  const selectedCount = Object.keys(selected).length;
  const totalLabels = Object.values(selected).reduce((sum, q) => sum + q, 0);

  const handlePrint = async () => {
    if (selectedCount === 0) {
      setError(t('barcode.noItemsSelected'));
      return;
    }
    setError(null);
    setPrinting(true);
    // Open the tab synchronously with the click so popup blockers allow it.
    const tab = window.open('', '_blank');
    try {
      const payloadItems = items
        .filter((item) => item.salesItemId in selected)
        .map((item) => ({ productId: item.productId, quantity: selected[item.salesItemId] }));
      await barcodeService.openBulkLabelsPdf(payloadItems, tab);
    } catch (err: unknown) {
      tab?.close();
      setError(err instanceof PdfDisplayError
        ? t('barcode.popupBlocked')
        : parseApiError(err, t('barcode.failedToGenerate')).message);
    } finally {
      setPrinting(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'select',
      label: '',
      width: '40px',
      render: (_value, item: SalesItemDTO) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={item.salesItemId in selected}
          onChange={() => toggleSelect(item)}
          aria-label={t('barcode.selectRow', { name: item.productName })}
        />
      ),
    },
    {
      key: 'productSKU',
      label: t('salesItem.productSKU'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'productName',
      label: t('salesItem.productName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'sellingPrice',
      label: t('salesItem.sellingPrice'),
      sortable: true,
      width: '130px',
      render: (value, row) => (
        <strong>{Number(value).toFixed(2)} {row.currency}</strong>
      ),
    },
    {
      key: 'salesChannel',
      label: t('salesItem.salesChannel'),
      sortable: true,
      width: '120px',
      hideTablet: true,
      render: (value) => <span className="badge badge-info">{value}</span>,
    },
    {
      key: 'quantity',
      label: t('barcode.quantityColumn'),
      width: '110px',
      render: (_value, item: SalesItemDTO) => (
        <input
          type="number"
          className="form-input"
          style={{ width: '80px' }}
          min={1}
          max={2000}
          disabled={!(item.salesItemId in selected)}
          value={selected[item.salesItemId] ?? 1}
          onChange={(e) => setQuantity(item.salesItemId, parseInt(e.target.value, 10))}
        />
      ),
    },
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">
            <Barcode size={20} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom', marginInlineEnd: '8px' }} />
            {t('barcode.bulkPrintTitle')}
          </h1>
          <div className="header-actions">
            <button type="button" className="btn-refresh" onClick={selectAll}>
              {t('barcode.selectAll')}
            </button>
            <button type="button" className="btn-refresh" onClick={clearSelection} disabled={selectedCount === 0}>
              {t('barcode.clearSelection')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('salesItem.refreshItems')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('salesItem.refreshing') : t('salesItem.refreshItems')}
            </button>
            <button
              type="button"
              className="btn-new"
              onClick={handlePrint}
              disabled={printing || selectedCount === 0}
            >
              {printing ? <span className="ims-btn-spinner" /> : <Printer size={16} strokeWidth={1.75} />}
              {t('barcode.printSelected')}
            </button>
          </div>
        </div>

        <p className="ims-form-hint" style={{ marginTop: 0 }}>{t('barcode.bulkPrintHint')}</p>

        {selectedCount > 0 && (
          <div className="alert" style={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #93c5fd',
            marginBottom: '16px',
          }}>
            {t('barcode.selectedSummary', { count: selectedCount, total: totalLabels })}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('salesItem.noItemsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default SalesItemBarcodePrintPage;
